import { Component, Input, OnInit, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { SkillStore } from "@app/services/store/skill-store";
import { routeAnimations } from "@app/core";
import { Skill } from "@app/models/rest/skill";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { SkillEditorComponent } from "@app/view/setup/assignment/skill-editor/skill-editor.component";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { ConfirmDialogComponent } from "@app/view/shared/confirm-dialog/confirm-dialog.component";
import { ResourceSkillStore } from "@app/services/store/resource-skill-store";
import { SkillRuleStore } from "@app/services/store/skill-rule-store";
import { MatToolbar } from "@angular/material/toolbar";
import { MatButton } from "@angular/material/button";
import { GridComponent } from "../../../shared/base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "app-skill-list",
  templateUrl: "./skill-list.component.html",
  styleUrls: ["./skill-list.component.scss"],
  animations: [routeAnimations],
  imports: [MatToolbar, MatButton, GridComponent, MatPaginator, TranslatePipe]
})
export class SkillListComponent
  extends BaseDataTablePageDirective<Skill>
  implements OnInit
{
  skillStore: SkillStore;
  resourceSkillStore = inject(ResourceSkillStore);
  skillRuleStore = inject(SkillRuleStore);
  protected store: Store<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;

  @Input()
  public columns: object[] = [
    // {
    //   field: 'id',
    //   text: 'ID',
    //   flex: 1,
    //   editor: false
    // },

    // {
    //   field: 'backgroundColor',
    //   text: 'BG',
    //   flex: 1,
    //   type: 'color'
    // },
    // {
    //   field: 'foregroundColor',
    //   text: 'FG',
    //   flex: 1,
    //   type: 'color'
    // },
    {
      field: "description",
      text: "Description",
      flex: 1,
      editor: true
    },
    {
      type: "action",
      text: "Assignments",
      width: 90,
      actions: [
        {
          cls: "fa fa-chevron-right",
          tooltip: "Assign Skill",
          onClick: ({ record }) => {
            if (isNaN(record.id)) {
              console.log("unsaved");
              this.serverErrorHandler.handleClientError(
                "Save changes before editing this record"
              );
              return;
            }
            console.log(`Editing ${record.name}`);
            console.log(this.router);
            this.router.navigate(["setup/assignment/skills", record.id]).then();
          }
        }
      ]
    }
    // {
    //   field: 'scoreWeight',
    //   text: 'Score Weight',
    //   flex: 1,
    //   editor: true,
    //   type: 'number'
    // }
  ];

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const skillStore = inject(SkillStore);
    const store = inject<Store<any>>(Store);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      skillStore,
      store,
      dialog,
      "Skill",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );

    this.skillStore = skillStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.gridStore.autoCommit = false;
    this.gridStore.load({});
  }

  addRow() {
    this.gridStore.insert(0, {
      code: "",
      description: "",
      scoreWeight: 1
    });
  }

  /**
   * Deleting a skill cascades to its resource skill assignments and to the skill
   * rules in skill profiles that reference it, so warn the user when any of the
   * skills they are about to delete are still assigned to operatives or used by a
   * skill profile.
   */
  override commit() {
    const removed: any[] = this.gridStore.changes?.removed ?? [];
    const removedSkills = removed.filter((record) => !isNaN(record.id));

    if (removedSkills.length === 0) {
      super.commit();
      return;
    }

    Promise.all(
      removedSkills.map((record) =>
        Promise.all([
          this.countAssignedOperatives(record),
          this.countSkillProfileRules(record)
        ]).then(([operatives, profileRules]) => ({
          record,
          operatives,
          profileRules
        }))
      )
    )
      .then((results) => {
        const affected = results.filter(
          (result) => result.operatives > 0 || result.profileRules > 0
        );
        if (affected.length === 0) {
          super.commit();
          return;
        }

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          data: {
            title: "Delete skills still in use?",
            message:
              "The following skills are still in use. Deleting them will also " +
              "remove those operative assignments and skill profile rules:",
            messages: affected.map((result) =>
              this.describeAffectedSkill(result)
            )
          },
          width: "500px"
        });

        dialogRef.afterClosed().subscribe((confirmed) => {
          if (confirmed === true) {
            super.commit();
          }
        });
      })
      .catch((reason) => {
        console.log("Failed to check skill usage", reason);
        // The check is only advisory; the server cleans up references either way
        super.commit();
      });
  }

  private describeAffectedSkill(result: {
    record: any;
    operatives: number;
    profileRules: number;
  }): string {
    const parts: string[] = [];
    if (result.operatives > 0) {
      parts.push(
        `${result.operatives} operative${result.operatives === 1 ? "" : "s"}`
      );
    }
    if (result.profileRules > 0) {
      parts.push(
        `${result.profileRules} skill profile rule${result.profileRules === 1 ? "" : "s"}`
      );
    }
    return `${result.record.data.description} — used by ${parts.join(" and ")}`;
  }

  private countAssignedOperatives(record: any): Promise<number> {
    const href = record.data?._links?.self?.href?.split("{")[0];
    if (!href) {
      return Promise.resolve(0);
    }
    return this.resourceSkillStore.countWithSearch("findBySkill", {
      pageParams: { page: 0, size: 1 },
      params: { skill: href }
    });
  }

  private countSkillProfileRules(record: any): Promise<number> {
    const href = record.data?._links?.self?.href?.split("{")[0];
    if (!href) {
      return Promise.resolve(0);
    }
    return this.skillRuleStore.countWithSearch("findBySkill", {
      pageParams: { page: 0, size: 1 },
      params: { skill: href }
    });
  }

  addSkill() {
    const dialogRef = this.dialog.open(SkillEditorComponent);

    dialogRef.afterClosed().subscribe((result) => {
      console.log("The dialog was closed");
      console.log(result);
      this.ngOnInit();
    });
  }
}
